// src/components/Icon.jsx
//
// The app's icon set. One family, stroke-only, on a 20px optical grid, drawn in
// `currentColor`.
//
// WHY THIS EXISTS
// ---------------
// Every icon in a fixed UI slot used to be a colour emoji character rendered as
// text: 31 sidebar nav items across three shells, the round buttons in panel
// headers, the dropzone, the due-date glyphs on My Care Plan, and 13 EmptyState
// call sites. Four things were wrong with that, none of them cosmetic:
//
//   1. The OS drew them, not us. 🩺 is a different illustration at a different
//      optical weight on macOS, Windows and Android, so the icon set was
//      whatever three vendors happened to ship.
//   2. They are off-palette. The system is navy / gold / cream; 🩸 is bright
//      red, 💊 orange, 🧲 red-and-grey, 🇦🇪 a flag.
//   3. Some have no glyph at all on older Android and Windows builds — 🫁 and
//      🧲 are recent additions and fall back to tofu.
//   4. THE IMPORTANT ONE: emoji ignore `currentColor`. An active nav item
//      tinted its label gold and left the emoji whatever colour Apple drew it;
//      a disabled item could not grey its icon; the dark sidebar got
//      light-background emoji sitting on navy. Every one of those is fixed for
//      free by a glyph that inherits colour.
//
// DELIBERATELY NOT CONVERTED
// --------------------------
// RECORD_TYPES (src/constants/recordTypes.js) keeps its emoji — 🩸 for a blood
// test, 💊 for a prescription — because that chip row is patient-facing and the
// colour emoji are genuinely more scannable there than a monochrome stroke set
// would be. Recognition beats consistency in that one place.
//
// Typographic symbols are also left alone: →, ←, ✓, ✕, ⚠ in prose and link
// text ("Message →") are punctuation, not icons, and converting them would be
// noise.
//
// ADDING A GLYPH
// --------------
// Stroke-only, `currentColor`, 1.6px, everything inside roughly x/y 3–17 so it
// sits at the same optical weight as its neighbours. Then LOOK AT IT AT SIZE,
// not just in the file: the first `vial` here was three units wide and rendered
// as a bare vertical stroke at 20px. A contact sheet at 3x plus one at native
// 16px is the check.
import React from 'react';

const ICONS = {
  // ── Patient nav ────────────────────────────────────────────────────────────
  /** Ask Previu, Team Chat, Chat — a speech bubble. */
  chat: (
    <>
      <path d="M4 6.5A1.5 1.5 0 0 1 5.5 5h9A1.5 1.5 0 0 1 16 6.5v5a1.5 1.5 0 0 1-1.5 1.5H8.5L5 16v-3H5.5A1.5 1.5 0 0 1 4 11.5v-5Z" />
    </>
  ),
  /** My Health Story — a marker trajectory over time. */
  trend: (
    <>
      <path d="M3.5 16.5V4" />
      <path d="M3.5 16.5H17" />
      <path d="M6 13l3-3.5 2.5 2L16 6.5" />
      <circle cx="16" cy="6.5" r="1.2" />
    </>
  ),
  /** My Documents — a folder. */
  folder: (
    <>
      <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h2.8a1.5 1.5 0 0 1 1.06.44L10 6.5h5a1.5 1.5 0 0 1 1.5 1.5v6.5A1.5 1.5 0 0 1 15 16H5a1.5 1.5 0 0 1-1.5-1.5v-8Z" />
    </>
  ),
  /** Lab Tests — a sample vial. Five units wide with a semicircular base; a
      narrower tube reads as a bare vertical stroke at this size. */
  vial: (
    <>
      <path d="M7 3.2h6.4" />
      <path d="M8.2 3.2v9.4a2.3 2.3 0 0 0 4.6 0V3.2" />
      <path d="M8.3 10.6h4.4" />
    </>
  ),
  /** Care Team — a stethoscope. */
  stethoscope: (
    <>
      <path d="M5 3.5v4a3.5 3.5 0 0 0 7 0v-4" />
      <path d="M3.6 3.5h2.8M10.6 3.5h2.8" />
      <path d="M8.5 11v1.5a3.5 3.5 0 0 0 3.5 3.5 3 3 0 0 0 3-3v-1.2" />
      <circle cx="15" cy="10.6" r="1.4" />
    </>
  ),
  /** My Appointments — a calendar. */
  calendar: (
    <>
      <path d="M4 6.5A1.5 1.5 0 0 1 5.5 5h9A1.5 1.5 0 0 1 16 6.5v8A1.5 1.5 0 0 1 14.5 16h-9A1.5 1.5 0 0 1 4 14.5v-8Z" />
      <path d="M4 8.5h12M7.5 3.5v3M12.5 3.5v3" />
    </>
  ),
  /** My Care Plan — a clipboard of assigned action items. */
  clipboard: (
    <>
      <path d="M8 4h4a1 1 0 0 1 1 1v1H7V5a1 1 0 0 1 1-1Z" />
      <path d="M7 6H5.5A1.5 1.5 0 0 0 4 7.5v8A1.5 1.5 0 0 0 5.5 17h9a1.5 1.5 0 0 0 1.5-1.5v-8A1.5 1.5 0 0 0 14.5 6H13" />
      <path d="M7.5 10.5h5M7.5 13.5h3" />
    </>
  ),
  /** Wallet & Billing. */
  wallet: (
    <>
      <path d="M3.5 7.5A1.5 1.5 0 0 1 5 6h10a1.5 1.5 0 0 1 1.5 1.5v6A1.5 1.5 0 0 1 15 15H5a1.5 1.5 0 0 1-1.5-1.5v-6Z" />
      <path d="M3.5 9.5h13" />
      <circle cx="13" cy="12.4" r="0.9" />
    </>
  ),
  /** Settings — sliders, NOT a gear.
      A cog's teeth turn to mud below ~24px, and the usual workaround (a circle
      with radial spokes) rendered as a sun here — verified on the contact
      sheet, it was unmistakably a sun. Sliders read unambiguously at 16px, are
      a current convention for settings and controls, and the nav item carries
      a "Settings" label beside it either way. */
  settings: (
    <>
      <path d="M3.5 6.2h13M3.5 10h13M3.5 13.8h13" />
      <circle cx="7.4" cy="6.2" r="1.7" />
      <circle cx="12.6" cy="10" r="1.7" />
      <circle cx="7.4" cy="13.8" r="1.7" />
    </>
  ),

  // ── Staff nav ──────────────────────────────────────────────────────────────
  /** Dashboard, Analytics — a bar chart. */
  chart: (
    <>
      <path d="M3.5 16.5V4" />
      <path d="M3.5 16.5H17" />
      <path d="M6.8 16.5v-4.2M10 16.5V8.5M13.2 16.5v-6" />
    </>
  ),
  /** Patients, My Patients — two people. */
  people: (
    <>
      <circle cx="8" cy="7.5" r="2.5" />
      <path d="M3.5 16.5c0-2.2 2-4 4.5-4s4.5 1.8 4.5 4" />
      <path d="M13.5 5.6a2.5 2.5 0 0 1 0 4.8" />
      <path d="M14.5 12.9c1.3.5 2.2 1.9 2.2 3.6" />
    </>
  ),
  /** Users — one person. */
  person: (
    <>
      <circle cx="10" cy="7" r="3" />
      <path d="M4.5 16.5c0-2.6 2.5-4.6 5.5-4.6s5.5 2 5.5 4.6" />
    </>
  ),
  /** Doctors — a clinic building with a cross. */
  hospital: (
    <>
      <path d="M4.5 16.5V5.5A1 1 0 0 1 5.5 4.5h9a1 1 0 0 1 1 1v11" />
      <path d="M3 16.5h14" />
      <path d="M10 7.6v4.2M7.9 9.7h4.2" />
    </>
  ),
  /** Import Patients — into a tray. */
  importInto: (
    <>
      <path d="M10 3.5v7.5" />
      <path d="M7 8.2l3 3 3-3" />
      <path d="M4 13v2.5A1 1 0 0 0 5 16.5h10a1 1 0 0 0 1-1V13" />
    </>
  ),
  /** Patient Messages — an envelope. */
  mail: (
    <>
      <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h10a1.5 1.5 0 0 1 1.5 1.5v7A1.5 1.5 0 0 1 15 15H5a1.5 1.5 0 0 1-1.5-1.5v-7Z" />
      <path d="M3.8 6.2 10 10.8l6.2-4.6" />
    </>
  ),
  /** Plan & Billing — a payment card. */
  card: (
    <>
      <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h10a1.5 1.5 0 0 1 1.5 1.5v7A1.5 1.5 0 0 1 15 14H5a1.5 1.5 0 0 1-1.5-1.5v-6Z" />
      <path d="M3.5 8.6h13" />
      <path d="M6.2 11.6h2.6" />
    </>
  ),
  /** Sign out. */
  logout: (
    <>
      <path d="M12 6V5a1 1 0 0 0-1-1H5.5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1H11a1 1 0 0 0 1-1v-1" />
      <path d="M8.5 10h8" />
      <path d="M14 7.5 16.5 10 14 12.5" />
    </>
  ),

  // ── Actions ────────────────────────────────────────────────────────────────
  /** New conversation. */
  plus: (
    <>
      <path d="M10 4.5v11M4.5 10h11" />
    </>
  ),
  /** Attach / drop a file. */
  paperclip: (
    <>
      <path d="M13.4 9.2l-4.6 4.6a2.4 2.4 0 0 0 3.4 3.4l4.6-4.6a4 4 0 0 0-5.6-5.6L6 12.2" />
    </>
  ),

  // ── Empty states, status ───────────────────────────────────────────────────
  /** Activity trail, "due soon". */
  clock: (
    <>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M10 6.2V10l2.8 1.7" />
    </>
  ),
  /** A single document. */
  document: (
    <>
      <path d="M5.5 4.5A1 1 0 0 1 6.5 3.5h4L14.5 7.5v9a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-12Z" />
      <path d="M10.4 3.6V7.5h3.9" />
    </>
  ),
  /** Notes. */
  note: (
    <>
      <path d="M5 4.5A1 1 0 0 1 6 3.5h8a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-11Z" />
      <path d="M7.5 7h5M7.5 10h5M7.5 13h3" />
    </>
  ),
  /** Something went wrong. */
  alert: (
    <>
      <path d="M10 3.8 17 15.8a1 1 0 0 1-.87 1.5H3.87A1 1 0 0 1 3 15.8L10 3.8Z" />
      <path d="M10 8.4v3.4" />
      <circle cx="10" cy="14.2" r="0.85" />
    </>
  ),
  /** Blood test / reports. */
  drop: (
    <>
      <path d="M10 3.2c2.6 3 4.5 5.4 4.5 7.6a4.5 4.5 0 0 1-9 0c0-2.2 1.9-4.6 4.5-7.6Z" />
    </>
  ),
  // ── Navigator / doctor surfaces ────────────────────────────────────────────
  /** Every search field in the app. */
  search: (
    <>
      <circle cx="8.8" cy="8.8" r="4.8" />
      <path d="M12.4 12.4 16.5 16.5" />
    </>
  ),
  /** Addresses, collection location. */
  pin: (
    <>
      <path d="M10 17s5.2-5 5.2-8.6a5.2 5.2 0 0 0-10.4 0C4.8 12 10 17 10 17Z" />
      <circle cx="10" cy="8.4" r="1.9" />
    </>
  ),
  /** Permission denied / restricted screens. */
  lock: (
    <>
      <path d="M5.2 9.2h9.6a1 1 0 0 1 1 1v5.3a1 1 0 0 1-1 1H5.2a1 1 0 0 1-1-1v-5.3a1 1 0 0 1 1-1Z" />
      <path d="M7 9.2V6.9a3 3 0 0 1 6 0v2.3" />
    </>
  ),
  /** Notifications. */
  bell: (
    <>
      <path d="M5.6 13.6V9.2a4.4 4.4 0 0 1 8.8 0v4.4l1.2 1.8H4.4l1.2-1.8Z" />
      <path d="M8.4 15.4a1.7 1.7 0 0 0 3.2 0" />
    </>
  ),
  /** Help. */
  help: (
    <>
      <circle cx="10" cy="10" r="6.8" />
      <path d="M8.2 8a1.9 1.9 0 0 1 3.7.6c0 1.3-1.9 1.5-1.9 2.7" />
      <circle cx="10" cy="14" r="0.8" />
    </>
  ),
  /** Theme toggle — shown while the portal is dark (switches to light). */
  sun: (
    <>
      <circle cx="10" cy="10" r="3.2" />
      <path d="M10 3v1.8M10 15.2V17M3 10h1.8M15.2 10H17M5.05 5.05l1.27 1.27M13.68 13.68l1.27 1.27M14.95 5.05l-1.27 1.27M6.32 13.68l-1.27 1.27" />
    </>
  ),
  /** Theme toggle — shown while the portal is light (switches to dark). */
  moon: (
    <>
      <path d="M16 11.6A6.4 6.4 0 0 1 8.4 4a6.4 6.4 0 1 0 7.6 7.6Z" />
    </>
  ),
  /** Contraindication — a thing the patient must NOT do. */
  ban: (
    <>
      <circle cx="10" cy="10" r="6.8" />
      <path d="M5.2 14.8 14.8 5.2" />
    </>
  ),
  /** Acute / urgent, where alert is already in use for something else. */
  bolt: (
    <>
      <path d="M11.2 3 5.4 11.4h3.6L8.4 17l5.9-8.5h-3.6L11.2 3Z" />
    </>
  ),
  /** Cardiac markers. */
  heart: (
    <>
      <path d="M10 16.2S3.6 12.4 3.6 8.1A3.6 3.6 0 0 1 10 6a3.6 3.6 0 0 1 6.4 2.1c0 4.3-6.4 8.1-6.4 8.1Z" />
    </>
  ),
  /** Contact details. */
  phone: (
    <>
      <path d="M6.2 3.8h2.4l1.2 3-1.7 1.2a8.4 8.4 0 0 0 3.9 3.9l1.2-1.7 3 1.2v2.4a1.4 1.4 0 0 1-1.5 1.4C9.4 15 5 10.6 4.8 5.3a1.4 1.4 0 0 1 1.4-1.5Z" />
    </>
  ),
  /** Video call — a human chat thread's header (message / video / voice). */
  video: (
    <>
      <rect x="3.3" y="6" width="7.4" height="8" rx="1.5" />
      <path d="M12.7 8.3 16.7 6v8l-4-2.3V8.3Z" />
    </>
  ),
  /** Medications, medical summary. */
  pill: (
    <>
      <path d="M6.9 13.1 13.1 6.9a3.4 3.4 0 1 1 4.8 4.8l-6.2 6.2a3.4 3.4 0 1 1-4.8-4.8Z" transform="translate(-2.5 -2.5)" />
      <path d="M7.5 7.5 12.5 12.5" />
    </>
  ),
  /** Navigator — the person steering the patient's care. */
  compass: (
    <>
      <circle cx="10" cy="10" r="6.8" />
      <path d="M12.6 7.4 8.9 8.9 7.4 12.6 11.1 11.1 12.6 7.4Z" />
    </>
  ),
  /** Card-action links — "View all", "View full breakdown". Replaces the
      ASCII "→" these used to end in: a real glyph aligns on its own baseline
      instead of sitting a few px off from the text, and it can shift on
      hover/focus the way a character can't. */
  chevronRight: (
    <>
      <path d="M7.5 4.5 13 10l-5.5 5.5" />
    </>
  ),
  /** Remove / clear a row — a lidded bin. */
  trash: (
    <>
      <path d="M4 5.5h12" />
      <path d="M8 5.5V4h4v1.5" />
      <path d="M5.5 5.5 6.3 16h7.4l.8-10.5" />
      <path d="M8.5 8.5v4.8M11.5 8.5v4.8" />
    </>
  ),
  /** Open / go to — a right-pointing arrow. */
  arrowRight: (
    <>
      <path d="M4 10h12" />
      <path d="M11.5 5.5 16 10l-4.5 4.5" />
    </>
  ),
  /** Save a file — an arrow into a tray. */
  download: (
    <>
      <path d="M10 3.5v9" />
      <path d="M6.5 9.5 10 13l3.5-3.5" />
      <path d="M4 16h12" />
    </>
  ),
  /** Re-fetch — two chasing arrows. */
  refresh: (
    <>
      <path d="M16 8A6.2 6.2 0 0 0 4.6 6.4" />
      <path d="M4.5 3v3.6h3.6" />
      <path d="M4 12a6.2 6.2 0 0 0 11.4 1.6" />
      <path d="M15.5 17v-3.6h-3.6" />
    </>
  ),
  /** Home collection — a house. */
  home: (
    <>
      <path d="M3.5 9.5 10 4l6.5 5.5" />
      <path d="M5 8.5V16h10V8.5" />
      <path d="M8.5 16v-4.5h3V16" />
    </>
  ),
  /** Booking / order confirmed. */
  check: (
    <>
      <circle cx="10" cy="10" r="6.8" />
      <path d="M6.9 10.2l2.2 2.2 4.1-4.6" />
    </>
  ),
  // ── Install guidance ───────────────────────────────────────────────────────
  /** iOS Share button — a box open at the top with an arrow leaving it. */
  share: (
    <>
      <path d="M10 12V3.5" />
      <path d="M7 6.5 10 3.5l3 3" />
      <path d="M6.5 9H5.5A1.5 1.5 0 0 0 4 10.5v5A1.5 1.5 0 0 0 5.5 17h9a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 14.5 9h-1" />
    </>
  ),
  /** "Add to Home Screen" — a rounded app tile with a plus. */
  addToHome: (
    <>
      <rect x="3.5" y="3.5" width="13" height="13" rx="3.2" />
      <path d="M10 7v6M7 10h6" />
    </>
  ),
  /** Android browser menu — three vertical dots. */
  menuDots: (
    <>
      <circle cx="10" cy="4.8" r="1" />
      <circle cx="10" cy="10" r="1" />
      <circle cx="10" cy="15.2" r="1" />
    </>
  ),
  /** Overflow menu ("more actions") — three horizontal dots. */
  moreDots: (
    <>
      <circle cx="4.8" cy="10" r="1" />
      <circle cx="10" cy="10" r="1" />
      <circle cx="15.2" cy="10" r="1" />
    </>
  ),
  /** Fallback, and "a list with nothing in it". */
  inbox: (
    <>
      <path d="M3.5 11.5 6 5.8A1.5 1.5 0 0 1 7.4 5h5.2a1.5 1.5 0 0 1 1.4.8l2.5 5.7" />
      <path d="M3.5 11.5h3.2l.9 1.8h4.8l.9-1.8h3.2v3A1.5 1.5 0 0 1 15 16H5a1.5 1.5 0 0 1-1.5-1.5v-3Z" />
    </>
  ),
};

/** Every name Icon accepts. Anything else is treated as an emoji by callers. */
export const ICON_NAMES = Object.keys(ICONS);

/**
 * @param name  one of ICON_NAMES; falls back to `inbox`
 * @param size  px, default 18 — nav slots and panel buttons both use that
 */
export default function Icon({ name, size = 18, className, style }) {
  const glyph = ICONS[name] || ICONS.inbox;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      style={style}
    >
      {glyph}
    </svg>
  );
}
